// View-once state for onboarding tours, persisted per browser and scoped by
// user id so a shared machine doesn't suppress a colleague's first run.
// Every read is guarded: private mode / blocked storage degrades to "the tour
// shows again", never to a crash.

const SEEN_KEY = 'jarvis_onboarding_seen';

type SeenMap = Record<string, string[]>;

/**
 * Storage scope for the seen flags. `authData.id` is the real user id; email is
 * the fallback for sessions that came in without one (some SSO paths). An
 * anonymous bucket is better than a shared one — worst case the tour repeats.
 */
export const getTourUserId = (): string | null => {
  try {
    const raw = window.localStorage.getItem('authData');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as { id?: number | string; email?: string };
    const id = parsed.id ?? parsed.email;
    return id === undefined || id === null || id === '' ? null : String(id);
  } catch {
    return null;
  }
};

const readSeenMap = (): SeenMap => {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const map: SeenMap = {};
    for (const [userId, tourIds] of Object.entries(parsed)) {
      if (Array.isArray(tourIds)) {
        map[userId] = tourIds.filter((id): id is string => typeof id === 'string');
      }
    }
    return map;
  } catch {
    return {};
  }
};

export const hasSeenTour = (userId: string, tourId: string): boolean =>
  readSeenMap()[userId]?.includes(tourId) ?? false;

export const markTourSeen = (userId: string, tourId: string) => {
  try {
    const map = readSeenMap();
    const seen = map[userId] ?? [];
    if (!seen.includes(tourId)) {
      map[userId] = [...seen, tourId];
      window.localStorage.setItem(SEEN_KEY, JSON.stringify(map));
    }
  } catch {
    // Storage unavailable — the tour will simply run again next visit.
  }
};

/** Clears every tour flag for every user in this browser. Dev/QA affordance. */
export const resetSeenTours = () => {
  try {
    window.localStorage.removeItem(SEEN_KEY);
  } catch {
    // Nothing to do — there was no usable storage to clear.
  }
};
